import { onlineManager } from '@tanstack/react-query';

/**
 * F1 — источник правды об онлайне: `navigator.onLine`, а не факт события.
 *
 * ЧТО ЛОМАЛОСЬ. Дефолтный детектор TanStack подписан на события `online`/`offline`
 * и верит им как состоянию: пришло `offline` — клиент офлайн, пока не придёт `online`.
 * Браузер эти события выдаёт не парами: после сна машины, смены Wi-Fi или переезда
 * VPN приходит `offline`, а обратного может не быть вовсе. С этого момента при
 * дефолтном `networkMode: 'online'` каждая мутация уходит в `paused`: `onMutate`
 * отрабатывает (оптимистик виден), `mutationFn` не вызывается никогда, ошибки нет,
 * тоста нет. Ровно это и наблюдалось на проде: «после долгой жизни вкладки смена
 * статуса молча не применяется, а после перезагрузки тот же клик работает».
 *
 * Воспроизведение (стенд, 09.08.2026): `window.dispatchEvent(new Event('offline'))`
 * при `navigator.onLine === true` → выбор статуса из поповера, шпион на `window.fetch`
 * пуст 2,5 с, ячейка уже показывает новый статус, тоста нет. `new Event('online')`
 * — и в шпионе немедленно появляется `PATCH /task-module/tasks/<id>`. То есть мутация
 * всё это время лежала в очереди paused.
 *
 * ЧТО ДЕЛАЕМ. Событие — только повод перечитать состояние, а состояние берём у
 * браузера (`navigator.onLine`). Плюс те же поводы, что переживают сон вкладки:
 * возврат фокуса, показ страницы из bfcache, смена видимости. Одиночное `offline`
 * при живой сети больше не переводит приложение в офлайн вообще.
 */
export function installOnlineTruth(): void {
  onlineManager.setEventListener((setOnline) => {
    const sync = (): void => {
      setOnline(typeof navigator === 'undefined' ? true : navigator.onLine);
    };
    window.addEventListener('online', sync);
    window.addEventListener('offline', sync);
    window.addEventListener('focus', sync);
    window.addEventListener('pageshow', sync);
    document.addEventListener('visibilitychange', sync);
    // Первичная синхронизация: вкладка могла быть открыта в момент, когда детектор
    // уже разошёлся с браузером (например, после восстановления из bfcache).
    sync();
    return () => {
      window.removeEventListener('online', sync);
      window.removeEventListener('offline', sync);
      window.removeEventListener('focus', sync);
      window.removeEventListener('pageshow', sync);
      document.removeEventListener('visibilitychange', sync);
    };
  });
}
